import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import Lenis from '@studio-freight/lenis';

// Lazy loaded page components for optimal bundle chunking
const Home = lazy(() => import('./pages/Home'));
const Contact = lazy(() => import('./pages/Contact'));
const BusinessEnquiries = lazy(() => import('./pages/BusinessEnquiries'));
const BecomeDeliveryPartner = lazy(() => import('./pages/BecomeDeliveryPartner'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Careers = lazy(() => import('./pages/Careers'));
const Media = lazy(() => import('./pages/Media'));
const Articles = lazy(() => import('./pages/Articles'));
const Awards = lazy(() => import('./pages/Awards'));
const CaseStudy = lazy(() => import('./pages/CaseStudy'));
const Technologies = lazy(() => import('./pages/Technologies'));
const GreenDriveDelivery = lazy(() => import('./pages/GreenDriveDelivery'));
const Services = lazy(() => import('./pages/Services'));
const Investor = lazy(() => import('./pages/Investor'));

// B2C Pages
const EvBikeRentals = lazy(() => import('./pages/EvBikeRentals'));
const CabBikeAutoBooking = lazy(() => import('./pages/CabBikeAutoBooking'));
const BusTrainBooking = lazy(() => import('./pages/BusTrainBooking'));
const PackersMovers = lazy(() => import('./pages/PackersMovers'));
const PackageDelivery = lazy(() => import('./pages/PackageDelivery'));
const EvChargerInstallation = lazy(() => import('./pages/EvChargerInstallation'));
const RailwaySolutions = lazy(() => import('./pages/RailwaySolutions'));

// B2B Pages
const FleetManagement = lazy(() => import('./pages/FleetManagement'));
const ContractFleet = lazy(() => import('./pages/ContractFleet'));
const OndcDelivery = lazy(() => import('./pages/OndcDelivery'));
const RailwayLogistics = lazy(() => import('./pages/RailwayLogistics'));
const IntercityDelivery = lazy(() => import('./pages/IntercityDelivery'));
const IndustriesServed = lazy(() => import('./pages/IndustriesServed'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-10 h-10 border-4 border-[#3869B3] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const Placeholder = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
    <h1 className="text-4xl font-bold text-[#3869B3] mb-4">Page Coming Soon</h1>
    <p className="text-gray-600">We are currently building this section.</p>
  </div>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
      lerp: 0.1,
      wheelMultiplier: 1.2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<MainLayout />}>
            <Route index element={<Home />} />
            <Route path="contact" element={<Contact />} />
            <Route path="partner">
              <Route path="business-partnership" element={<BusinessEnquiries />} />
              <Route path="become-a-delivery-partner" element={<BecomeDeliveryPartner />} />
            </Route>
            <Route path="about" element={<AboutUs />} />
            <Route path="technologies" element={<Technologies />} />
            <Route path="services" element={<Services />} />
            <Route path="green-drive-delivery" element={<GreenDriveDelivery />} />
            <Route path="careers" element={<Careers />} />
            <Route path="investor" element={<Investor />} />
            <Route path="investors" element={<Investor />} />
            <Route path="media" element={<Media />} />
            <Route path="resources">
              <Route path="articles-insights" element={<Articles />} />
              <Route path="case-study" element={<CaseStudy />} />
              <Route path="awards-recognition" element={<Awards />} />
            </Route>
            
            {/* Solution Routes */}
            <Route path="solutions/b2c/ev-bike-rentals" element={<EvBikeRentals />} />
            <Route path="solutions/b2c/cab-bike-auto-booking" element={<CabBikeAutoBooking />} />
            <Route path="solutions/b2c/bus-train-booking" element={<BusTrainBooking />} />
            <Route path="solutions/b2c/packers-movers" element={<PackersMovers />} />
            <Route path="solutions/b2c/package-delivery" element={<PackageDelivery />} />
            <Route path="solutions/b2c/ev-charger-installation" element={<EvChargerInstallation />} />
            <Route path="solutions/b2c/railway-solutions" element={<RailwaySolutions />} />
            
            <Route path="solutions/b2b/fleet-management" element={<FleetManagement />} />
            <Route path="solutions/b2b/contract-fleet" element={<ContractFleet />} />
            <Route path="solutions/b2b/ondc-delivery" element={<OndcDelivery />} />
            <Route path="solutions/b2b/railway-logistics" element={<RailwayLogistics />} />
            <Route path="solutions/b2b/intercity-delivery" element={<IntercityDelivery />} />
            <Route path="solutions/industries-served" element={<IndustriesServed />} />
            {/* Catch-all route for missing pages so the Header still shows */}
            <Route path="*" element={<Placeholder />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;

